/** tm/legal/LegalBillDetail.tsx — 국회 법안 상세 (GET /bills/:id): 의안 정보 · 제안이유·주요내용 · 진행 단계(정부 제출 법률안이면 입법예고·국무회의부터) · 「의안 상세 보기」 · 별표 · 안 읽음으로 */
import { useCallback, useEffect, useState } from 'react';
import { C, T } from '../components/ui';
import { getBill, setBillRead, setBillStar } from './legalApi';
import { btn, shortDate, whenLabel, safeHttp, errMsg, AMBER, MUTED } from './legalTheme';
import { BillStatusBadge } from './legalUi';
import { DetailHead, MetaGrid, Box, DetailEmpty, DetailSkeleton, DETAIL_MAX, actsRow } from './LegalDetailParts';
import LegalStageTimeline from './LegalStageTimeline';
import type { BillDetail, Selection } from './legalTypes';

interface Props {
  id: number; mobile: boolean;
  onBack: () => void; onToast: (m: string, k?: 'ok' | 'error') => void;
  onPatch: (p: { starred?: boolean; unread?: boolean }) => void;
  onOpen?: (s: NonNullable<Selection>) => void;
}

export default function LegalBillDetail({ id, mobile, onBack, onToast, onPatch, onOpen }: Props) {
  const [b, setB] = useState<BillDetail | null>(null);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    setB(null); setErr('');
    try { setB(await getBill(id)); } catch (e) { setErr(errMsg(e, '법안을 불러오지 못했습니다')); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  if (err) return <>{mobile && <button type="button" className="lw-f" onClick={onBack} style={btn()}>← 목록</button>}<DetailEmpty error={err} onRetry={load} /></>;
  if (!b) return <DetailSkeleton />;

  const star = async () => {
    const next = !b.starred;
    setB({ ...b, starred: next }); onPatch({ starred: next });
    try { await setBillStar(b.id, next); onToast(next ? '별표했습니다' : '별표를 해제했습니다'); }
    catch (e) { setB({ ...b, starred: !next }); onPatch({ starred: !next }); onToast(errMsg(e, '별표하지 못했습니다'), 'error'); }
  };
  const unread = async () => {
    onPatch({ unread: true });
    try { await setBillRead(b.id, false); onToast('안 읽음으로 바꿨습니다'); }
    catch (e) { onPatch({ unread: false }); onToast(errMsg(e, '바꾸지 못했습니다'), 'error'); }
  };
  const url = safeHttp(b.detail_url);

  return (
    <div>
      <DetailHead group="국회 법안" label="국회 법안" topics={b.topics || []} title={b.title} mobile={mobile} onBack={onBack} stage="bill"
        extra={<BillStatusBadge status={b.status} />} />
      <MetaGrid mobile={mobile} rows={[
        ['의안번호', b.bill_no || '-'],
        ['상태', <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 10px' }}><BillStatusBadge status={b.status} />{b.status_changed_at && <span style={{ fontSize: T.sm, color: MUTED, whiteSpace: 'nowrap' }}>{whenLabel(b.status_changed_at)} 바뀜</span>}</span>],
        ['소관 위원회', b.committee || '위원회 회부 전'],
        ['발의자', b.proposer || '-'],
        ['발의일', shortDate(b.propose_dt)],
      ]} />
      {b.summary
        ? <Box title="제안이유 · 주요내용">{b.summary}</Box>
        : <Box kind="q" title="제안이유 · 주요내용">요약을 아직 받지 못했습니다. 「의안 상세 보기」에서 확인해 주세요.</Box>}
      <LegalStageTimeline kind="bill" id={b.id} onOpen={onOpen} />
      <p style={{ fontSize: T.sm, color: MUTED, maxWidth: DETAIL_MAX, lineHeight: 1.6, margin: '0 0 12px' }}>
        법안 상태가 바뀌면(위원회 심사 → 본회의 부의 → 가결·폐기) 다시 새 소식으로 알려 드립니다. 가결된 법률은 공포되면 「법령」 탭에 올라옵니다.
      </p>
      <div style={actsRow}>
        {url && <a className="lw-f" href={url} target="_blank" rel="noopener noreferrer" style={btn('pri')}>의안 상세 보기</a>}
        <button type="button" className="lw-f" onClick={star} aria-pressed={b.starred} style={{ ...btn(), color: b.starred ? AMBER : C.sub }}>{b.starred ? '★ 별표 해제' : '☆ 별표'}</button>
        <button type="button" className="lw-f" onClick={unread} style={btn()}>안 읽음으로</button>
      </div>
    </div>
  );
}
